import { Button } from "design-system/atoms/Button"
import {
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "design-system/atoms/Dialog"
import { RadioGroup, RadioGroupItem } from "design-system/atoms/RadioGroup"
import { cn } from "design-system/utils"
import { FileCheck, FileUp, ListFilter, Loader2 } from "lucide-react"
import { useCampaignLaunchAudienceQuery } from "modules/campaigns/hooks/useCampaignLaunchAudienceQuery"
import { AudienceRulesDraft, toAudienceSource } from "modules/campaigns/lib/launchAudienceRules"
import { ImportFileField } from "modules/customers/components/Import/ImportFileField"
import { ImportStartProps } from "modules/customers/components/Import/ImportFlow"
import { useCustomerImportStatusQuery } from "modules/customers/hooks/useCustomerImportStatusQuery"
import { useImportCustomersMutation } from "modules/customers/hooks/useImportCustomersMutation"
import { useDebouncedValue } from "modules/shared/hooks/useDebouncedValue"
import { useMemo } from "react"
import { useTranslation } from "react-i18next"
import { CampaignLaunchAudience } from "schema"
import { AudienceRulesBuilder } from "./AudienceRulesBuilder"

export type AudienceChoice = "rules" | "file"

type Props = ImportStartProps & {
  campaignId: string
  pendingImportId: string | undefined
  choice: AudienceChoice
  rules: AudienceRulesDraft
  onChoiceChange: (choice: AudienceChoice) => void
  onRulesChange: (rules: AudienceRulesDraft) => void
  onUseImport: (importId: string) => void
  onContinueWithRules: (audienceSource: CampaignLaunchAudience) => void
  onClose: () => void
}

export function LaunchAudienceStep({
  campaignId,
  pendingImportId,
  choice,
  rules,
  fileForm,
  onChoiceChange,
  onRulesChange,
  onActionResponse,
  onUseImport,
  onContinueWithRules,
  onClose,
}: Props) {
  const { t } = useTranslation()

  const audienceSource = useMemo(() => toAudienceSource(rules), [rules])
  const debouncedAudienceSource = useDebouncedValue({ value: audienceSource })

  const { data: audience, isFetching: isCounting } = useCampaignLaunchAudienceQuery({
    campaignId,
    audienceSource: choice === "rules" ? debouncedAudienceSource : undefined,
  })

  const { mutateAsync: uploadCustomersImport, isPending: isUploading } = useImportCustomersMutation(
    { onSuccess: onActionResponse },
  )

  const isCountReady =
    Boolean(audienceSource) && !isCounting && audienceSource === debouncedAudienceSource
  const canContinue =
    choice === "file"
      ? Boolean(fileForm.watch("file")?.length) && !isUploading
      : isCountReady && Boolean(audience?.audience)

  function handleContinue() {
    if (choice === "file") {
      uploadCustomersImport({ preview: "true", file: fileForm.watch("file") })
      return
    }

    if (audienceSource) onContinueWithRules(audienceSource)
  }

  const options = [
    {
      value: "rules" as const,
      icon: ListFilter,
      title: t("campaigns.launchAudience.choice.rules.title"),
      description: t("campaigns.launchAudience.choice.rules.description"),
    },
    {
      value: "file" as const,
      icon: FileUp,
      title: t("campaigns.launchAudience.choice.file.title"),
      description: t("campaigns.launchAudience.choice.file.description"),
    },
  ]

  return (
    <>
      <DialogHeader>
        <DialogTitle>{t("campaigns.launchAudience.title")}</DialogTitle>
        <DialogDescription>{t("campaigns.launchAudience.description")}</DialogDescription>
      </DialogHeader>
      <div className="flex flex-col gap-4">
        {pendingImportId && (
          <PendingImportShortcut importId={pendingImportId} onUseImport={onUseImport} />
        )}
        <RadioGroup
          value={choice}
          onValueChange={(value) => onChoiceChange(value as AudienceChoice)}
          className="grid-cols-1 gap-3 sm:grid-cols-2"
        >
          {options.map((option) => (
            <label
              key={option.value}
              className={cn(
                "flex cursor-pointer items-start gap-3 rounded-lg border p-3.5 transition-colors",
                choice === option.value ? "border-primary shadow-xs" : "hover:bg-muted",
              )}
            >
              <span className="grid size-8 shrink-0 place-items-center rounded-md bg-secondary">
                <option.icon className="size-4" />
              </span>
              <span className="flex min-w-0 flex-1 flex-col gap-0.5">
                <span className="flex items-center justify-between gap-2 text-sm font-medium">
                  {option.title}
                  <RadioGroupItem value={option.value} />
                </span>
                <span className="text-sm text-muted-foreground">{option.description}</span>
              </span>
            </label>
          ))}
        </RadioGroup>
        {choice === "rules" ? (
          <AudienceRulesBuilder value={rules} onChange={onRulesChange} />
        ) : (
          <ImportFileField fileForm={fileForm} />
        )}
      </div>
      <DialogFooter className="sm:items-center">
        {choice === "rules" && (
          <div className="text-sm sm:mr-auto">
            {!audienceSource && (
              <span className="text-muted-foreground">{t("campaigns.launchAudience.pickOne")}</span>
            )}
            {audienceSource && !isCountReady && (
              <span className="inline-flex items-center gap-2 text-muted-foreground">
                <Loader2 className="size-4 animate-spin" />
                {t("campaigns.launchAudience.counting")}
              </span>
            )}
            {audienceSource && isCountReady && audience && (
              <span className="flex flex-col leading-tight">
                <span>{t("campaigns.launchAudience.matching", { count: audience.audience })}</span>
                <span className="text-xs text-muted-foreground">
                  {t("campaigns.launchAudience.optedOut", { count: audience.optedOutCount })}
                </span>
              </span>
            )}
          </div>
        )}
        <Button variant="outline" onClick={onClose}>
          {t("shared.cancel")}
        </Button>
        <Button onClick={handleContinue} disabled={!canContinue}>
          {t("shared.continue")}
        </Button>
      </DialogFooter>
    </>
  )
}

type PendingImportShortcutProps = {
  importId: string
  onUseImport: (importId: string) => void
}

function PendingImportShortcut({ importId, onUseImport }: PendingImportShortcutProps) {
  const { t } = useTranslation()
  const { data } = useCustomerImportStatusQuery(importId)

  if (data?.status !== "COMPLETED") return null

  return (
    <div className="flex flex-wrap items-center gap-3 rounded-lg border bg-muted p-3">
      <FileCheck className="size-4 shrink-0 text-muted-foreground" />
      <div className="flex min-w-0 flex-1 flex-col">
        <span className="text-sm font-medium">{t("campaigns.pendingImport.completedTitle")}</span>
        <span className="text-sm text-muted-foreground">
          {t("campaigns.pendingImport.completedDescription", {
            count: data.createdCount + data.updatedCount,
          })}
        </span>
      </div>
      <Button variant="outline" size="sm" onClick={() => onUseImport(importId)}>
        {t("campaigns.launchAudience.useImport")}
      </Button>
    </div>
  )
}
